import { Close, Edit } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, IconButton, TextField } from "@mui/material";
import Modal from "@mui/material/Modal";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useState } from "react";
import { useToasts } from "react-toast-notifications";
import theme from "../../theme/theme";
import { BookingDiv, CardTitle, CardWrapper, CrossContainer } from "./styles";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { EditReservationModalProps } from "./types";
import { putReservations } from "../../services/reservations/putReservations";
import { getResourceById } from "../../services/resources/getResources";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getTime } from "../../services/time/getTime";

const style = {
  display: "flex",
  position: "relative",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "30%",
  height: "auto",
  minWidth: "350px",
  maxWidth: "430px",
  maxHeight: "700px",
  overflow: "auto",
  borderRadius: "20px",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  zIndex: 100,
  "@media (max-width: 420px)": {
    width: "80%",
    minWidth: "300px",
    p: 2,
  },
};

export const EditReservationModal: React.FC<EditReservationModalProps> = ({
  id,
  resource,
  currentStartDate,
  currentStartTime,
  currentEndDate,
  currentEndTime,
  currentDescription,
}) => {
  const jwt = localStorage.getItem("jwt");

  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = React.useState<Dayjs | null>(null);
  const [endDate, setEndDate] = React.useState<Dayjs | null>(null);
  const [startTime, setStartTime] = React.useState<Dayjs | null>(null);
  const [endTime, setEndTime] = React.useState<Dayjs | null>(null);
  const [start, setStart] = React.useState<string | null>(null);
  const [end, setEnd] = React.useState<string | null>(null);
  const [serverResponse, setServerResponse] = React.useState(false); // Server response state
  const [description, setDescription] = React.useState(""); // Resource description state
  const [restrictHours, setRestrictHours] = React.useState<boolean>(false);
  const { addToast } = useToasts();

  const [isPosted, setIsPosted] = React.useState(false);
  const [invalid, setInvalid] = React.useState(false);
  const [loadingRequest, setLoadingRequest] = React.useState(false);
  const [currentDate, setCurrentDate] = React.useState<Dayjs | null>(null);

  // ------------------- GET RESOURCES FROM API -------------------
  const editReservation = async () => {
    if (start && end) {
      await putReservations(jwt!, id, "", "", start, end, description, resource)
        .then((res: any) => {
          if (res && res.data) {
            if (res.status > 299) {
              setLoadingRequest(false);
              addToast("Modificación rechazada", {
                appearance: "error",
                autoDismiss: true,
                zIndex: 9999,
                containerProps: {
                  style: {
                    zIndex: 9999,
                  },
                },
              });
            } else {
              setLoadingRequest(false);
              addToast("Modificación exitosa", {
                appearance: "success",
                autoDismiss: true,
                zIndex: 9999,
                containerProps: {
                  style: {
                    zIndex: 9999,
                  },
                },
              });
              handleClose();
              setTimeout(() => {
                window.location.reload();
              }, 1500); // 2000 milisegundos = 2 segundos
            }
          }
        })
        .catch((err: any) => {
          setLoadingRequest(false);
          addToast("Modificación rechazada", {
            appearance: "error",
            autoDismiss: true,
            zIndex: 9999,

            containerProps: {
              style: {
                zIndex: 9999,
              },
            },
          });
        });
    }
  };

  const getCurrentTime = async () => {
    await getTime(jwt!)
      .then((res: any) => {
        if (res && res.data) {
          // setCurrentTime(convertTimeToDayjs(res.data.server_time));
          setCurrentDate(convertDateToDayjs(res.data.server_time));
        }
      })
      .catch((err: any) => {
        console.log("Error: ", err);
      });
  };

  // const getResource = async () => {
  //   await getResourceById(reservationId, jwt!)
  //     .then((res) => {
  //       if (res && res.data) {
  //         console.log("Reservación: ", res.data);
  //         setDescription(res.data.description);

  //         const startTimeObject = convertStringToDayjs(res.data.start);
  //         const endTimeObject = convertStringToDayjs(res.data.finish);

  //         if (startTimeObject !== null) {
  //           setStartDate(dayjs(startTimeObject.date).tz(timeZone));
  //           setStartTime(dayjs(startTimeObject.time).tz(timeZone));
  //         }

  //         if (endTimeObject !== null) {
  //           setEndDate(dayjs(endTimeObject.date).tz(timeZone));
  //           setEndTime(dayjs(endTimeObject.time).tz(timeZone));
  //         }
  //       }
  //     })
  //     .catch((err) => {
  //       console.log(err, "err");
  //     });
  // };

  const timeZone = "America/Mexico_City";
  dayjs.tz.setDefault("America/Mexico_City");

  const convertTimeCurr2DB = (
    date: Dayjs | null,
    time: Dayjs | null
  ): string => {
    if (!date || !time) return "";

    // Combina la fecha y la hora en un solo objeto Dayjs
    const combinedDateTime = date
      .hour(time.hour())
      .minute(time.minute())
      .second(time.second());

    // Convierte la fecha y hora ajustadas a UTC
    // const utcAdjustedDateTime = setUTCDate(combinedDateTime);

    // Formatea la fecha y hora en el formato deseado
    const formattedDate = combinedDateTime.format("YYYY-MM-DD HH:mm:ss.SSS");
    return formattedDate;
  };

  const setUTCDate = (date: dayjs.Dayjs): dayjs.Dayjs => {
    const utcDate = date.tz(timeZone).utc();
    return utcDate;
  };

  function convertDateToDayjs(utcDate: string): Dayjs {
    const mexicoCityTime = dayjs(utcDate)
      .utc() // Indica que la entrada es UTC
      .subtract(5, "hour") // Resta 6 horas para la zona horaria de Ciudad de México
      .tz("America/Mexico_City"); // Convierte a la zona horaria de Ciudad de México
    return mexicoCityTime;
  }

  const NewReservation = () => {
    if (!startDate || !endDate || !startTime || !endTime || !description) {
      const missingFields = [];
      if (!startDate) missingFields.push("Fecha de inicio");
      if (!endDate) missingFields.push("Fecha de fin");
      if (!startTime) missingFields.push("Hora de inicio");
      if (!endTime) missingFields.push("Hora de fin");
      if (!description) missingFields.push("Descripción");

      const missingFieldsMessage = `Faltan llenar los siguientes campos: ${missingFields.join(
        ", "
      )}`;

      addToast(missingFieldsMessage, {
        appearance: "error",
        autoDismiss: true,
        zIndex: 9999,
        containerProps: {
          style: {
            zIndex: 9999,
          },
        },
      });


      return;
    }

    if (startDate && endDate && startTime && endTime) {
      if (startDate.isAfter(endDate)) {
        addToast(
          `Error al hacer la reserva: la fecha ${endDate.format(
            "DD/MM/YYYY"
          )} de fin es anterior a la de inicio`,
          {
            appearance: "error",
            autoDismiss: true,
            zIndex: 9999,
            containerProps: {
              style: {
                zIndex: 9999,
              },
            },
          }
        );
        return;
      }

      const minTime = currentDate?.hour(7).minute(0).second(0); // 7:00 AM
      const maxTime = currentDate?.hour(22).minute(0).second(0); // 10:00 PM

      if (
        startTime.hour() < 7 ||
        startTime.hour() >= 22 ||
        endTime.hour() < 7 ||
        endTime.hour() > 22
      ) {
        addToast(
          `Error al hacer la reserva: La hora ingresada debe estar entre las 7 AM y las 10 PM.`,
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        return;
      }

      if (
        startDate.isBefore(currentDate) &&
        !startDate.isSame(currentDate, "day")
      ) {
        addToast(
          `Error al hacer la reserva: la fecha ${startDate.format(
            "DD/MM/YYYY"
          )} de inicio no es válida`,
          {
            appearance: "error",
            autoDismiss: true,
            zIndex: 9999,
            containerProps: {
              style: {
                zIndex: 9999,
              },
            },
          }
        );
        return;
      }

      const startDateTime = setUTCDate(startDate)
        .hour(startTime.hour())
        .minute(startTime.minute())
        .second(startTime.second());

      const endDateTime = setUTCDate(endDate)
        .hour(endTime.hour())
        .minute(endTime.minute())
        .second(endTime.second());

      if (endDateTime.isBefore(startDateTime)) {
        addToast(
          `Error al hacer la reserva: la hora de fin es anterior a la de inicio`,
          {
            appearance: "error",
            autoDismiss: true,
            zIndex: 9999,
            containerProps: {
              style: {
                zIndex: 9999,
              },
            },
          }
        );
        return;
      }

      if (
        (startDate.isBefore(currentDate) &&
          !startDate.isSame(currentDate, "day")) ||
        (startDate.isSame(currentDate) &&
          startTime.isBefore(currentDate) &&
          !startTime.isSame(currentDate, "day"))
      ) {
        addToast(`Error al hacer la reserva: la hora de inicio no es válida`, {
          appearance: "error",
          autoDismiss: true,
          zIndex: 9999,
          containerProps: {
            style: {
              zIndex: 9999,
            },
          },
        });
        return;
      }

      setLoadingRequest(true);
      editReservation();

      // Borra la información de los DatePickers y TimePickers después de hacer la reserva
      setStartDate(null);
      setEndDate(null);
      setStartTime(null);
      setEndTime(null);
      setDescription("");

    }
  };

  // ------------------- DateTime functions -------------------

  dayjs.extend(utc);
  dayjs.extend(timezone);

  interface DateTimeResult {
    date: string;
    time: string;
  }

  useEffect(() => {
    const convertDateToDayjs = (dateString: string): dayjs.Dayjs | null => {
      const parsedDate = dayjs(dateString, "DD/MM/YY");
      if (parsedDate.isValid()) {
        return parsedDate;
      }
      return null; // Devuelve null si el formato es inválido
    };

    const convertTimeToDayjs = (timeString: string): dayjs.Dayjs | null => {
      const parsedTime = dayjs(timeString, "HH:mm");
      if (parsedTime.isValid()) {
        return parsedTime;
      }
      return null; // Devuelve null si el formato es inválido
    };

    setStartDate(convertDateToDayjs(currentStartDate));
    setStartTime(convertTimeToDayjs(currentStartTime));
    setEndDate(convertDateToDayjs(currentEndDate));
    setEndTime(convertTimeToDayjs(currentEndTime));
    currentDescription
      ? setDescription(currentDescription)
      : setDescription("");
  }, []);

  useEffect(() => {
    if (startDate && endDate && startTime && endTime) {
      const startDateTime = convertTimeCurr2DB(startDate, startTime);
      const endDateTime = convertTimeCurr2DB(endDate, endTime);
      setStart(startDateTime);
      setEnd(endDateTime);
    }
  }, [startDate, endDate, startTime, endTime]);

  // useEffect(() => {
  //   if (startDate && endDate && startDate.isSame(endDate, "day")) {
  //     // Si la fecha de inicio y fin son el mismo día, establece la hora de inicio igual a la hora de fin
  //     setStartTime(endTime);
  //   } else {
  //     setStartTime(null);
  //     setEndTime(null);
  //   }
  // }, [startDate, endDate]);

  useEffect(() => {
    getCurrentTime();
  }, []);

  const handleTimePickerChange = (
    newValue: Dayjs | null,
    isStartTime: boolean
  ) => {
    if (newValue) {
      if (isStartTime) {
        setStartTime(newValue);
      } else {
        setEndTime(newValue);
      }
    }
  };

  const handleOpen = () => {
    setOpen(true);
    // getReservations();
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <Button
        variant="contained"
        startIcon={<Edit />}
        sx={{
          width: "100%",
          borderRadius: "12px",
          fontSize: "18px",
          backgroundColor: theme.color.stone,
          color: "#FFFFFF",
          textTransform: "none",
          "&:hover": {
            backgroundColor: theme.color.graphite,
          },
        }}
        onClick={handleOpen}
      >
        Editar reservación
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <CrossContainer onClick={handleClose}>
          <IconButton><Close /></IconButton>
          </CrossContainer>
          <CardWrapper>
            <CardTitle>Editar Reservación</CardTitle>
            <BookingDiv>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fecha de inicio"
                  value={startDate}
                  minDate={currentDate ? currentDate : undefined} // Disable dates before the current date
                  onChange={(newValue: Dayjs | null) => setStartDate(newValue)}
                  slotProps={{
                    textField: {
                      required: true,
                      sx: { marginRight: "-1px" },
                      InputProps: {
                        style: {
                          borderRadius: "10px 0 0 10px", // Agregar el borderRadius aquí
                        },
                      },
                    },
                  }}
                />
                <DatePicker
                  label="Fecha de fin"
                  value={endDate}
                  minDate={currentDate ? currentDate : undefined} // Disable dates before the current date
                  onChange={(newValue: Dayjs | null) => setEndDate(newValue)}
                  slotProps={{
                    textField: {
                      required: true,
                      InputProps: {
                        style: {
                          borderRadius: "0 10px 10px 0", // Agregar el borderRadius aquí
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </BookingDiv>
            <BookingDiv>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  label="Hora de inicio"
                  value={startTime}
                  minTime={
                    currentDate?.isBefore(currentDate.hour(7).minute(0)) // Comprueba si la hora actual es antes de las 7 AM
                      ? currentDate?.hour(7).minute(0) // Establece 7 AM como hora mínima
                      : startDate && startDate.isSame(currentDate, "day")
                      ? currentDate?.add(1, "minute")
                      : currentDate?.hour(7).minute(0)
                  }
                  maxTime={dayjs().hour(22).minute(0)}
                  onChange={(newValue: Dayjs | null) => {
                    handleTimePickerChange(newValue, true);
                  }}
                  slotProps={{
                    textField: {
                      required: true,
                      sx: { marginRight: "-1px" },
                      InputProps: {
                        style: {
                          borderRadius: "10px 0 0 10px",
                          width: "100%",
                        },
                      },
                    },
                  }}
                  // Deshabilita AM si restrictHours es true
                  ampm={false}
                />
                <TimePicker
                  label="Hora de fin"
                  value={endTime}
                  minTime={
                    currentDate?.isBefore(currentDate.hour(7).minute(0)) // Comprueba si la hora actual es antes de las 7 AM
                      ? currentDate?.hour(7).minute(0) // Establece 7 AM como hora mínima
                      : startDate && startDate.isSame(currentDate, "day")
                      ? currentDate?.add(1, "minute")
                      : currentDate?.hour(7).minute(0)
                  }
                  maxTime={dayjs().hour(22).minute(0)}
                  onChange={(newValue: Dayjs | null) => {
                    handleTimePickerChange(newValue, false);
                  }}
                  slotProps={{
                    textField: {
                      required: true,
                      InputProps: {
                        style: {
                          borderRadius: "0 10px 10px 0",
                        },
                      },
                    },
                  }}
                  ampm={false}
                />
              </LocalizationProvider>
            </BookingDiv>
            <BookingDiv>
              <TextField
                label="Descripción"
                value={description}
                required
                multiline
                rows={2}
                defaultValue=""
                variant="outlined"
                fullWidth
                onChange={(e) => setDescription(e.target.value)}
                InputProps={{
                  style: {
                    borderRadius: "10px",
                  },
                }}
              />
            </BookingDiv>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button
                variant="contained"
                sx={{
                  width: "100%",
                  marginTop: "0.8rem",
                  borderRadius: "15px",
                  fontSize: "16px",
                  backgroundColor: theme.color.navy,
                  color: theme.color.white,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: theme.color.space,
                  },
                }}
                onClick={NewReservation}
                disabled={loadingRequest}
              >
                Editar reservación
              </Button>
              {loadingRequest && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: theme.color.navy,
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-6px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>

            <Alert
              severity="info"
              sx={{
                borderRadius: "10px",
                margin: "1.5rem 0 -0.5rem 0",
                fontSize: "11px",
              }}
            >
              Los horarios de reservación se muestran en la zona horaria del Hub
              de Ciberseguridad <strong>(Ciudad de México)</strong>
            </Alert>
          </CardWrapper>
        </Box>
      </Modal>
    </div>
  );
};
